import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usersApi } from '../api/users';
import useAuthStore from '../store/authStore';
import toast from 'react-hot-toast';
import { LazyUserForm } from '../components/Forms/LazyForms';

const Users = () => {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const queryClient = useQueryClient();
  const { hasPermission } = useAuthStore();

  const { data, isLoading, error } = useQuery({
    queryKey: ['users', page, pageSize],
    queryFn: () => usersApi.getAll({ page, pageSize }),
  });

  const deleteMutation = useMutation({
    mutationFn: usersApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('Usuario eliminado exitosamente');
    },
    onError: (error) => {
      // Manejar errores específicos del servidor
      if (error.response?.status === 409) {
        // Error de conflicto - usuario tiene datos relacionados
        const message = error.response?.data?.message || 'No se puede eliminar el usuario porque tiene datos relacionados';
        toast.error(message);
      } else {
        // Otros errores
        toast.error(error.message || 'Error al eliminar usuario');
      }
    },
  });

  console.log('Users data:', data);
  console.log('Users error:', error);

  // API can return array directly or wrapped in { data: [] }
  const users = Array.isArray(data) ? data : (data?.data || []);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
        {hasPermission('canManageUsers') && (
          <button 
            className="btn btn--primary" 
            onClick={() => {
              setEditingUser(null);
              setShowForm(true);
            }}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '18px', marginRight: '8px' }}>add</span>
            Nuevo Usuario
          </button>
        )}
      </div>

      <div style={{ marginTop: 0 }}>
        {isLoading ? (
          <div style={{ padding: '40px', textAlign: 'center', opacity: 0.7 }}>Cargando...</div>
        ) : error ? (
          <div style={{ padding: '40px', textAlign: 'center', opacity: 0.7, color: '#ef4444' }}>
            Error al cargar usuarios: {error.message || 'Error desconocido'}
          </div>
        ) : users.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', opacity: 0.7 }}>No hay usuarios disponibles</div>
        ) : (
          <div style={{ overflowX: 'auto', borderRadius: '12px', border: '1px solid rgba(255,255,255,.10)', background: 'var(--panel-bg)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
              <thead>
                <tr style={{ background: 'rgba(255,255,255,.07)' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '700', letterSpacing: '.3px', color: '#f2f2f2' }}>Nombre</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '700', letterSpacing: '.3px', color: '#f2f2f2' }}>Email</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '700', letterSpacing: '.3px', color: '#f2f2f2' }}>Rol</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '700', letterSpacing: '.3px', color: '#f2f2f2' }}>Estado</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '700', letterSpacing: '.3px', color: '#f2f2f2' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} style={{ borderTop: '1px solid rgba(255,255,255,.08)', color: '#e5e5e5' }}>
                    <td style={{ padding: '12px 16px', fontWeight: '600' }}>{user.nombre || '-'}</td>
                    <td style={{ padding: '12px 16px' }}>{user.email}</td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <span className={`badge ${
                        user.role === 'admin' ? 'green' : 
                        user.role === 'DESPACHADOR' ? 'gray' : 
                        'gray'
                      }`}>
                        {user.role?.toUpperCase() || '-'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <span className={`badge ${user.activo === 1 || user.activo === true ? 'green' : 'red'}`}>
                        {user.activo === 1 || user.activo === true ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      {hasPermission('canManageUsers') ? (
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center' }}>
                          {/* Editar */}
                          <div style={{ position: 'relative', display: 'inline-block' }}>
                            <button
                              onClick={() => {
                                setEditingUser(user);
                                setShowForm(true);
                              }}
                              style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '8px',
                                border: 'none',
                                background: '#3b82f6',
                                color: '#fff',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: 0,
                                transition: 'all 0.2s',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#2563eb';
                                e.currentTarget.style.transform = 'scale(1.05)';
                                const tooltip = e.currentTarget.nextElementSibling;
                                if (tooltip) tooltip.style.opacity = '1';
                                if (tooltip) tooltip.style.visibility = 'visible';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = '#3b82f6';
                                e.currentTarget.style.transform = 'scale(1)';
                                const tooltip = e.currentTarget.nextElementSibling;
                                if (tooltip) tooltip.style.opacity = '0';
                                if (tooltip) tooltip.style.visibility = 'hidden';
                              }}
                            >
                              <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>edit</span>
                            </button>
                            <div
                              style={{
                                position: 'absolute',
                                bottom: '100%',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                marginBottom: '8px',
                                padding: '6px 12px',
                                background: '#1a1a1a',
                                color: '#fff',
                                fontSize: '12px',
                                borderRadius: '6px',
                                whiteSpace: 'nowrap',
                                pointerEvents: 'none',
                                opacity: 0,
                                visibility: 'hidden',
                                transition: 'opacity 0.2s, visibility 0.2s',
                                zIndex: 1000,
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                              }}
                            >
                              Editar
                              <div
                                style={{
                                  position: 'absolute',
                                  top: '100%',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  width: 0,
                                  height: 0,
                                  borderLeft: '6px solid transparent',
                                  borderRight: '6px solid transparent',
                                  borderTop: '6px solid #1a1a1a'
                                }}
                              />
                            </div>
                          </div>

                          {/* Eliminar */}
                          <div style={{ position: 'relative', display: 'inline-block' }}>
                            <button
                              onClick={() => {
                                if (confirm('¿Estás seguro de que quieres eliminar este usuario?')) {
                                  deleteMutation.mutate(user.id);
                                }
                              }}
                              disabled={deleteMutation.isPending}
                              style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '8px',
                                border: 'none',
                                background: '#ef4444',
                                color: '#fff',
                                cursor: deleteMutation.isPending ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: 0,
                                transition: 'all 0.2s',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                opacity: deleteMutation.isPending ? 0.6 : 1
                              }}
                              onMouseEnter={(e) => {
                                if (!deleteMutation.isPending) {
                                  e.currentTarget.style.background = '#dc2626';
                                  e.currentTarget.style.transform = 'scale(1.05)';
                                  const tooltip = e.currentTarget.nextElementSibling;
                                  if (tooltip) tooltip.style.opacity = '1';
                                  if (tooltip) tooltip.style.visibility = 'visible';
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = '#ef4444';
                                e.currentTarget.style.transform = 'scale(1)';
                                const tooltip = e.currentTarget.nextElementSibling;
                                if (tooltip) tooltip.style.opacity = '0';
                                if (tooltip) tooltip.style.visibility = 'hidden';
                              }}
                            >
                              <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>delete</span>
                            </button>
                            <div
                              style={{
                                position: 'absolute',
                                bottom: '100%',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                marginBottom: '8px',
                                padding: '6px 12px',
                                background: '#1a1a1a',
                                color: '#fff',
                                fontSize: '12px',
                                borderRadius: '6px',
                                whiteSpace: 'nowrap',
                                pointerEvents: 'none',
                                opacity: 0,
                                visibility: 'hidden',
                                transition: 'opacity 0.2s, visibility 0.2s',
                                zIndex: 1000,
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                              }}
                            >
                              Eliminar
                              <div
                                style={{
                                  position: 'absolute',
                                  top: '100%',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  width: 0,
                                  height: 0,
                                  borderLeft: '6px solid transparent',
                                  borderRight: '6px solid transparent',
                                  borderTop: '6px solid #1a1a1a'
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <span style={{ opacity: 0.5, fontSize: '14px' }}>Sin permisos</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <LazyUserForm
          user={editingUser}
          onClose={() => {
            setShowForm(false);
            setEditingUser(null);
          }}
          onSuccess={() => {
            setShowForm(false);
            setEditingUser(null);
          }}
        />
      )}
    </div>
  );
};

export default Users;
